import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuth } from '@/context/AuthContext';
import RoleGate from '@/components/layout/RoleGate';
import { eventsService } from '@/services/events.service';
import { eventCommentsAPI, type EventComment } from '@/services/eventComments.service';
import { QUICK_REACT_EMOJIS } from '@/config/emoji-config';
import { formatMessageTime } from '@/lib/chatTime';
import EventFormModal from './EventFormModal';
import EventLocationRow from './EventLocationRow';
import RsvpSummary from './rsvp/RsvpSummary';
import RsvpList from './rsvp/RsvpList';
import AttachmentList from './attachments/AttachmentList';
import AttachmentUploader from './attachments/AttachmentUploader';
import { hasRoleAtLeast } from '@/components/layout/RoleGate';

const RSVP_OPTIONS = [
  { value: 'yes', label: 'Going', activeClass: 'bg-green-600 text-white border-green-600' },
  { value: 'maybe', label: 'Maybe', activeClass: 'bg-amber-500 text-white border-amber-500' },
  { value: 'no', label: "Can't go", activeClass: 'bg-flock-navy-600 text-white border-flock-navy-600' },
] as const;

function CommentRow({
  comment,
  eventId,
  currentUserId,
  onChanged,
}: {
  comment: EventComment;
  eventId: string;
  currentUserId: string | undefined;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(comment.text);
  const own = comment.author.id === currentUserId;

  return (
    <div className="rounded-xl bg-white p-3 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-flock-navy-700">{comment.author.name}</span>
        <span className="text-[10px] text-flock-navy-400">
          {formatMessageTime(new Date(comment.createdAt))}
          {comment.editedAt ? ' · edited' : ''}
        </span>
      </div>

      {editing ? (
        <div className="mt-1">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none"
          />
          <div className="mt-1 flex gap-2">
            <button
              onClick={async () => {
                await eventCommentsAPI.editComment(eventId, comment.id, text.trim()).catch(() => {});
                setEditing(false);
                onChanged();
              }}
              className="rounded-lg bg-flock-navy-600 px-3 py-1 text-xs text-white"
            >
              Save
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded-lg border border-flock-gray-200 px-3 py-1 text-xs text-flock-navy-600"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-1 whitespace-pre-wrap text-sm text-flock-navy-900">{comment.text}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1">
        {Object.entries(comment.reactions || {})
          .filter(([, uids]) => uids.length > 0)
          .map(([emoji, uids]) => (
            <button
              key={emoji}
              onClick={async () => {
                await eventCommentsAPI.reactToComment(eventId, comment.id, emoji).catch(() => {});
                onChanged();
              }}
              className="rounded-full border border-flock-gray-200 px-2 py-0.5 text-xs"
            >
              {emoji} {uids.length}
            </button>
          ))}
        <div className="flex gap-0.5">
          {QUICK_REACT_EMOJIS.slice(0, 3).map((emoji) => (
            <button
              key={emoji}
              onClick={async () => {
                await eventCommentsAPI.reactToComment(eventId, comment.id, emoji).catch(() => {});
                onChanged();
              }}
              className="rounded p-0.5 text-sm opacity-40 transition hover:opacity-100"
              title={`React ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
        {own && !editing && (
          <div className="ml-auto flex gap-2 text-[11px] text-flock-navy-400">
            <button onClick={() => setEditing(true)} className="hover:text-flock-navy-700">
              Edit
            </button>
            <button
              onClick={async () => {
                if (!window.confirm('Delete this comment?')) return;
                await eventCommentsAPI.deleteComment(eventId, comment.id).catch(() => {});
                onChanged();
              }}
              className="hover:text-red-600"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, currentOrganization, organizations, role, switchOrganization } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [commentText, setCommentText] = useState('');

  // Cross-org calendar links arrive as /events/:id?org=<orgId> — discussion
  // and comments are org-scoped, so switch into the event's org first.
  const targetOrgId = searchParams.get('org');
  const needsOrgSwitch = Boolean(
    targetOrgId &&
      targetOrgId !== currentOrganization?.id &&
      organizations.some((o) => o.organization_id === targetOrgId && o.status === 'active')
  );
  useEffect(() => {
    if (!needsOrgSwitch || !targetOrgId) return;
    void switchOrganization(targetOrgId).then(() => {
      setSearchParams({}, { replace: true });
    });
  }, [needsOrgSwitch, targetOrgId, switchOrganization, setSearchParams]);

  const eventQuery = useQuery({
    queryKey: [currentOrganization?.id, 'event', eventId],
    enabled: Boolean(eventId) && !needsOrgSwitch,
    queryFn: () => eventsService.getEvent(eventId!),
  });

  const rsvpsQuery = useQuery({
    queryKey: [currentOrganization?.id, 'event-rsvps', eventId],
    enabled: Boolean(eventId),
    queryFn: () => eventsService.getEventRSVPs(eventId!),
  });

  const commentsQuery = useQuery({
    queryKey: [currentOrganization?.id, 'event-comments', eventId],
    enabled: Boolean(eventId),
    queryFn: () => eventCommentsAPI.listComments(eventId!, { limit: 50 }),
  });

  const attachmentsQuery = useQuery({
    queryKey: [currentOrganization?.id, 'event-attachments', eventId],
    enabled: Boolean(eventId),
    queryFn: () => eventsService.getAttachments(eventId!),
  });

  const rsvpMutation = useMutation({
    mutationFn: (status: 'yes' | 'maybe' | 'no') => eventsService.submitRSVP(eventId!, status),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [currentOrganization?.id, 'event', eventId] });
      queryClient.invalidateQueries({ queryKey: [currentOrganization?.id, 'event-rsvps', eventId] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
  });

  const event = eventQuery.data;
  const rsvpData = rsvpsQuery.data;
  const comments = commentsQuery.data?.data.comments ?? [];

  if (eventQuery.isLoading || needsOrgSwitch) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
        Loading event…
      </div>
    );
  }
  if (!event) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
        Event not found.
      </div>
    );
  }

  const start = new Date(event.start_time);
  const end = event.end_time ? new Date(event.end_time) : null;
  const isPast = start.getTime() < Date.now();

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b border-flock-gray-200 bg-white px-6 py-4">
        <button
          onClick={() => navigate('/events')}
          className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-flock-navy-50"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-semibold text-flock-navy-900">{event.title}</h1>
          <p className="text-xs text-flock-navy-400">
            {formatMessageTime(start)}
            {end ? ` – ${end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : ''}
          </p>
        </div>
        <RoleGate minRole="group_leader">
          <button
            onClick={() => setEditing(true)}
            title="Edit event"
            className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-flock-navy-50"
          >
            <PencilSquareIcon className="h-5 w-5" />
          </button>
          <button
            onClick={async () => {
              if (!window.confirm('Delete this event?')) return;
              await eventsService.deleteEvent(event.id).catch(() => {});
              navigate('/events');
            }}
            title="Delete event"
            className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-red-50 hover:text-red-600"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </RoleGate>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-4">
          <div className="rounded-2xl border border-flock-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-flock-navy-600">
              <span className="flex items-center gap-1.5">
                <CalendarDaysIcon className="h-4 w-4" /> {formatMessageTime(start)}
              </span>
              {event.location && (
                <EventLocationRow event={event} className="text-flock-navy-600" />
              )}
              {event.recurrence && event.recurrence !== 'none' && (
                <span className="capitalize text-flock-navy-400">repeats {event.recurrence}</span>
              )}
            </div>
            {event.description && (
              <p className="mt-3 whitespace-pre-wrap text-sm text-flock-navy-900">
                {event.description}
              </p>
            )}

            {!isPast && (
              <div className="mt-4">
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
                  Will you be there?
                </div>
                <div className="flex gap-2">
                  {RSVP_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      disabled={rsvpMutation.isPending}
                      onClick={() => rsvpMutation.mutate(opt.value)}
                      className={clsx(
                        'min-h-touch flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition',
                        event.user_rsvp === opt.value
                          ? opt.activeClass
                          : 'border-flock-gray-200 text-flock-navy-600 hover:bg-flock-navy-50'
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {((attachmentsQuery.data?.length ?? 0) > 0 ||
            (hasRoleAtLeast(role, 'group_leader') && !isPast)) && (
            <div className="rounded-2xl border border-flock-gray-200 bg-white p-4 shadow-sm">
              <h2 className="mb-3 text-sm font-semibold text-flock-navy-900">Attachments</h2>
              <AttachmentList eventId={event.id} attachments={attachmentsQuery.data ?? []} />
              <RoleGate minRole="group_leader">
                <div className={clsx((attachmentsQuery.data?.length ?? 0) > 0 && 'mt-3')}>
                  <AttachmentUploader eventId={event.id} />
                </div>
              </RoleGate>
            </div>
          )}

          {rsvpData && (
            <div className="rounded-2xl border border-flock-gray-200 bg-white p-4 shadow-sm">
              <h2 className="mb-3 text-sm font-semibold text-flock-navy-900">Who&apos;s coming</h2>
              <RsvpSummary counts={rsvpData.counts} />
              {rsvpData.attendanceTaken && rsvpData.attendanceCounts && (
                <p className="mt-2 text-xs text-flock-navy-500">
                  Attendance taken: {rsvpData.attendanceCounts.present} present ·{' '}
                  {rsvpData.attendanceCounts.absent} absent
                </p>
              )}
              <div className="mt-4">
                <RsvpList
                  eventId={event.id}
                  rsvps={rsvpData.rsvps}
                  counts={rsvpData.counts}
                  canNudge={!isPast && hasRoleAtLeast(role, 'group_leader')}
                />
              </div>
            </div>
          )}

          <div>
            <h2 className="mb-2 text-sm font-semibold text-flock-navy-900">
              Discussion {comments.length > 0 ? `(${comments.length})` : ''}
            </h2>
            <div className="space-y-2">
              {comments.map((comment) => (
                <CommentRow
                  key={comment.id}
                  comment={comment}
                  eventId={event.id}
                  currentUserId={user?.id}
                  onChanged={() => commentsQuery.refetch()}
                />
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && commentText.trim()) {
                    await eventCommentsAPI.addComment(event.id, commentText.trim()).catch(() => {});
                    setCommentText('');
                    commentsQuery.refetch();
                  }
                }}
                placeholder="Add to the discussion…"
                className="min-w-0 flex-1 rounded-lg border border-flock-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
              />
              <button
                onClick={async () => {
                  if (!commentText.trim()) return;
                  await eventCommentsAPI.addComment(event.id, commentText.trim()).catch(() => {});
                  setCommentText('');
                  commentsQuery.refetch();
                }}
                disabled={!commentText.trim()}
                className="rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white hover:bg-flock-navy-700 disabled:opacity-50"
              >
                Post
              </button>
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <EventFormModal
          groupId={event.group_id}
          event={event}
          onSaved={() => eventQuery.refetch()}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}
